/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from antd/lib/transfer/list.tsx
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import omit from 'rc-util/lib/omit';
import classNames from 'classnames';
import { Checkbox } from '../checkbox';
import { Icon } from '../icon';
import { Menu } from '../menu';
import Dropdown from '../dropdown';
import Search from './Search';
// import DefaultListBody, { TransferListBodyProps, OmitProps } from 'antd/lib/transfer/ListBody';
import DefaultListBody, { OmitProps } from './ListBody';
import { isValidElement } from 'antd/lib/_util/reactNode';
const defaultRender = () => null;
function isRenderResultPlainObject(result) {
    return result && !isValidElement(result) && Object.prototype.toString.call(result) === '[object Object]';
}
function getEnabledItemKeys(items) {
    return items.filter((data) => !data.disabled).map((data) => data.key);
}
export default class TransferList extends React.PureComponent {
    static defaultProps = {
        dataSource: [],
        titleText: '',
        showSearch: false
    };
    timer;
    triggerScrollTimer;
    defaultListBodyRef = React.createRef();
    constructor(props) {
        super(props);
        this.state = {
            filterValue: ''
        };
    }
    componentWillUnmount() {
        clearTimeout(this.triggerScrollTimer);
    }
    getCheckStatus(filteredItems) {
        const { checkedKeys } = this.props;
        if (checkedKeys.length === 0) {
            return 'none';
        }
        if (filteredItems.every((item) => checkedKeys.indexOf(item.key) >= 0 || !!item.disabled)) {
            return 'all';
        }
        return 'part';
    }
    // ================================ Item ================================
    getFilteredItems(dataSource, filterValue) {
        const filteredItems = [];
        const filteredRenderItems = [];
        dataSource.forEach((item) => {
            const renderedItem = this.renderItem(item);
            const { renderedText } = renderedItem;
            // Filter skip
            if (filterValue && !this.matchFilter(renderedText, item)) {
                return null;
            }
            filteredItems.push(item);
            filteredRenderItems.push(renderedItem);
        });
        return { filteredItems, filteredRenderItems };
    }
    // =============================== Filter ===============================
    handleFilter = (e) => {
        const { handleFilter } = this.props;
        const { target: { value: filterValue } } = e;
        this.setState({ filterValue });
        handleFilter(e);
    };
    handleClear = () => {
        const { handleClear } = this.props;
        this.setState({ filterValue: '' });
        handleClear();
    };
    matchFilter = (text, item) => {
        const { filterValue } = this.state;
        const { filterOption } = this.props;
        if (filterOption) {
            return filterOption(filterValue, item);
        }
        return text.indexOf(filterValue) >= 0;
    };
    getCurrentPageItems = () => { };
    // =============================== Render ===============================
    renderListBody = (renderList, props) => {
        let bodyContent = renderList ? renderList(props) : null;
        const customize = !!bodyContent;
        if (!customize) {
            bodyContent = React.createElement(DefaultListBody, { ref: this.defaultListBodyRef, ...props, footerDom: props.footerDom });
        }
        return {
            customize,
            bodyContent
        };
    };
    getListBody(prefixCls, searchPlaceholder, filterValue, filteredItems, notFoundContent, filteredRenderItems, checkedKeys, renderList, showSearch, disabled, footerDom) {
        const search = showSearch ? (React.createElement("div", { className: `${prefixCls}-body-search-wrapper` },
            React.createElement(Search, { prefixCls: `${prefixCls}-search`, 
                // @ts-ignore
                onChange: this.handleFilter, handleClear: this.handleClear, placeholder: searchPlaceholder, value: filterValue, disabled: disabled }))) : null;
        const { bodyContent, customize } = this.renderListBody(renderList, {
            ...omit(this.props, OmitProps),
            filteredItems,
            filteredRenderItems,
            selectedKeys: checkedKeys,
            footerDom: footerDom
        });
        const getNotFoundContent = () => {
            const contentIndex = this.props.direction === 'left' ? 0 : 1;
            return Array.isArray(notFoundContent) ? notFoundContent[contentIndex] : notFoundContent;
        };
        let bodyNode;
        // We should wrap customize list body in a classNamed div to use flex layout.
        if (customize) {
            bodyNode = React.createElement("div", { className: `${prefixCls}-body-customize-wrapper` }, bodyContent);
        }
        else {
            bodyNode = filteredItems.length ? (bodyContent) : (React.createElement("div", { className: `${prefixCls}-body-not-found` }, getNotFoundContent()));
        }
        return (React.createElement("div", { className: classNames(showSearch ? `${prefixCls}-body ${prefixCls}-body-with-search` : `${prefixCls}-body`) },
            search,
            bodyNode));
    }
    getCheckBox({ filteredItems, onItemSelectAll, disabled, prefixCls }) {
        const checkStatus = this.getCheckStatus(filteredItems);
        const checkedAll = checkStatus === 'all';
        const checkAllCheckbox = (React.createElement(Checkbox, { disabled: disabled, checked: checkedAll, indeterminate: checkStatus === 'part', className: `${prefixCls}-checkbox`, onChange: () => {
                // Only select enabled items
                onItemSelectAll(filteredItems.filter((item) => !item.disabled).map(({ key }) => key), !checkedAll);
            } }));
        return checkAllCheckbox;
    }
    renderItem = (item) => {
        const { render = defaultRender } = this.props;
        const renderResult = render(item);
        const isRenderResultPlain = isRenderResultPlainObject(renderResult);
        return {
            renderedText: isRenderResultPlain ? renderResult.value : renderResult,
            renderedEl: isRenderResultPlain ? renderResult.label : renderResult,
            item
        };
    };
    getSelectAllLabel = (selectedCount, totalCount) => {
        const { itemsUnit, itemUnit, selectAllLabel } = this.props;
        if (selectAllLabel) {
            return typeof selectAllLabel === 'function'
                ? selectAllLabel({ selectedCount, totalCount })
                : selectAllLabel;
        }
        const unit = totalCount > 1 ? itemsUnit : itemUnit;
        return (React.createElement(React.Fragment, null,
            (selectedCount > 0 ? `${selectedCount}/` : '') + totalCount,
            " ",
            unit));
    };
    render() {
        const { filterValue } = this.state;
        const { prefixCls, dataSource, titleText, checkedKeys, disabled, footer, showSearch, style, searchPlaceholder, notFoundContent, selectAll, selectCurrent, selectInvert, removeAll, removeCurrent, renderList, onItemSelectAll, onItemRemove, showSelectAll = true, showRemove, pagination, direction } = this.props;
        // Custom Layout
        const footerDom = footer && (footer.length < 2 ? footer(this.props) : footer(this.props, { direction }));
        const listCls = classNames(prefixCls, {
            [`${prefixCls}-with-pagination`]: !!pagination,
            [`${prefixCls}-with-footer`]: !!footerDom,
            [`${prefixCls}-with-search`]: !!showSearch
        });
        // ====================== Get filtered, checked item list ======================
        const { filteredItems, filteredRenderItems } = this.getFilteredItems(dataSource, filterValue);
        const checkAllCheckbox = !showRemove && !pagination && this.getCheckBox({ filteredItems, onItemSelectAll, disabled, prefixCls });
        let menu = null;
        if (showRemove) {
            menu = (React.createElement(Menu, null,
                pagination && (React.createElement(Menu.Item, { onClick: () => {
                        const pageKeys = getEnabledItemKeys((this.defaultListBodyRef.current?.getItems() || []).map((entity) => entity.item));
                        onItemRemove?.(pageKeys);
                    } }, removeCurrent)),
                React.createElement(Menu.Item, { onClick: () => {
                        onItemRemove?.(getEnabledItemKeys(filteredItems));
                    } }, removeAll)));
        }
        else {
            menu = (React.createElement(Menu, null,
                React.createElement(Menu.Item, { onClick: () => {
                        const keys = getEnabledItemKeys(filteredItems);
                        onItemSelectAll(keys, keys.length !== checkedKeys.length);
                    } }, selectAll),
                pagination && (React.createElement(Menu.Item, { onClick: () => {
                        const pageItems = this.defaultListBodyRef.current?.getItems() || [];
                        onItemSelectAll(getEnabledItemKeys(pageItems.map((entity) => entity.item)), true);
                    } }, selectCurrent)),
                React.createElement(Menu.Item, { onClick: () => {
                        let availableKeys;
                        if (pagination) {
                            availableKeys = getEnabledItemKeys((this.defaultListBodyRef.current?.getItems() || []).map((entity) => entity.item));
                        }
                        else {
                            availableKeys = getEnabledItemKeys(filteredItems);
                        }
                        const checkedKeySet = new Set(checkedKeys);
                        const newCheckedKeys = [];
                        const newUnCheckedKeys = [];
                        availableKeys.forEach((key) => {
                            if (checkedKeySet.has(key)) {
                                newUnCheckedKeys.push(key);
                            }
                            else {
                                newCheckedKeys.push(key);
                            }
                        });
                        onItemSelectAll(newCheckedKeys, true);
                        onItemSelectAll(newUnCheckedKeys, false);
                    } }, selectInvert)));
        }
        // Replaced with B Design components.
        const dropdown = (React.createElement(Dropdown, { className: `${prefixCls}-header-dropdown g-layout-margin-left-2 g-layout-margin-right-2`, overlay: menu, disabled: disabled },
            React.createElement(Icon, { type: 'chevron--down', size: 'small' })));
        const customized = !!renderList;
        // ================================ List Footer ================================
        const selectDom = (React.createElement(React.Fragment, null,
            showSelectAll ? (React.createElement(React.Fragment, null,
                checkAllCheckbox,
                dropdown)) : null,
            React.createElement("span", { className: `${prefixCls}-footer-selected` }, this.getSelectAllLabel(checkedKeys.length, filteredItems.length))));
        const mergedFooterDom = footerDom ? footerDom : selectDom;
        // const listFooter = <div className={`${prefixCls}-footer`}>{mergedFooterDom}</div>;
        // ================================= List Body =================================
        const listBody = this.getListBody(prefixCls, searchPlaceholder, filterValue, filteredItems, notFoundContent, filteredRenderItems, checkedKeys, renderList, showSearch, disabled, mergedFooterDom);
        // ================================= List Header =================================
        const listHeader = titleText || customized ? (React.createElement("div", { className: `${prefixCls}-header` },
            React.createElement("span", { className: `${prefixCls}-header-title` },
                customized ? selectDom : null,
                titleText))) : null;
        // ================================== Render ===================================
        return (React.createElement("div", { className: listCls, style: style },
            listHeader,
            listBody));
    }
}
